/**
 * Notification type registry.
 *
 * This is the SINGLE place to register a new notification type. The bell UI is
 * fully driven by this registry — adding a new event from a future producer
 * (e.g. Plan §4 / §5 / §7) only requires:
 *
 *   1. Add a new entry to `NOTIFICATION_TYPES` below (icon, color, title key,
 *      and optionally a route resolver).
 *   2. Add the matching i18n key under `messages.<dotted.type>` in the
 *      `notifications` namespace (en + it).
 *
 * No changes to `NotificationBell.tsx` are needed.
 *
 * @userstory US-NOTIF-02, US-NOTIF-03
 * @userstory US-MON-10 (monitor.talent_available)
 * @userstory US-MON-T05 (monitor.talent_contacted_company)
 */
import type { TColor } from '../../type/color-type';
import { UserType } from '../../common/data/dummyRolesData';

/** Context passed to a descriptor's `buildRoute` so it can produce a deep link. */
export interface NotificationRouteContext {
	/** Free-form payload from the backend (string-to-string map). */
	data: Record<string, string>;
	/** Current user role (used to disambiguate types that apply to both flows). */
	role: UserType | null | undefined;
}

/**
 * Declarative descriptor for one notification type. Everything the UI needs to
 * render and route a notification is here — no per-type branching in the bell.
 */
export interface NotificationTypeDescriptor {
	/** Material icon name (rendered via the `<Icon>` component). */
	icon: string;
	/** Optional accent color for the icon. Defaults to `primary`. */
	color?: TColor;
	/**
	 * Primary line — i18n key in the `notifications` namespace. The notification's
	 * `data` map is spread as merge variables, so the translation can interpolate
	 * any `{{key}}` whose name matches a key in `data` (e.g. `{{talentName}}`).
	 */
	titleKey: string;
	/**
	 * Optional secondary line — same merge-var rules as `titleKey`. Use this for
	 * payload-driven content like a message preview, an opportunity title, etc.
	 * If omitted, only the title row is rendered.
	 */
	bodyKey?: string;
	/**
	 * Optional deep-link resolver. Return `null` to leave the row non-clickable
	 * (it will still be marked-as-read on click — see bell behavior).
	 */
	buildRoute?: (ctx: NotificationRouteContext) => string | null;
}

/**
 * Fallback descriptor used when an unknown type is received. Keeps the inbox
 * resilient to backend additions that ship before the FE is updated.
 */
export const GENERIC_DESCRIPTOR: NotificationTypeDescriptor = {
	icon: 'Notifications',
	color: 'primary',
	titleKey: 'messages.generic',
};

/**
 * Registry of known notification types. Add new entries here.
 *
 * Convention: backend event type strings are dotted, snake_case
 * (`<area>.<event>`). The key here MUST match exactly the value of
 * `NotificationEventTypes` constants on the backend.
 */
export const NOTIFICATION_TYPES: Record<string, NotificationTypeDescriptor> = {
	// ── Monitor & Availability loop (Plan §3) ─────────────────────────────
	'monitor.talent_available': {
		icon: 'Visibility',
		color: 'primary',
		titleKey: 'messages.monitor.talent_available',
		// Open the public talent profile (shared route `talents/:id`). The
		// payload `talentUserId` comes from MonitorNotificationPublisher.
		buildRoute: ({ data }) =>
			data.talentUserId ? `/talents/${data.talentUserId}` : '/company/monitors',
	},
	'monitor.talent_contacted_company': {
		icon: 'Chat',
		color: 'info',
		titleKey: 'messages.monitor.talent_contacted_company',
		// Render the talent's message preview (passed in `data.messagePreview`
		// by the backend producer) as a secondary line in the panel.
		bodyKey: 'messages.monitor.talent_contacted_company_body',
		// Open the company-side chat scoped to this talent. Convention shared
		// with TalentCard / PublicTalentProfile entry points.
		buildRoute: ({ data }) =>
			data.talentUserId ? `/company/chat?talent=${data.talentUserId}` : '/company/chat',
	},

	// ── Saved search auto-refresh (Plan §7) ───────────────────────────────
	/** @userstory US-EXP-33 */
	'saved_search.new_matches': {
		icon: 'Search',
		color: 'success',
		titleKey: 'messages.saved_search.new_matches',
		bodyKey: 'messages.saved_search.new_matches_body',
		buildRoute: ({ data }) =>
			data.searchId ? `/company/searches/${data.searchId}` : '/company/searches',
	},
	/** @userstory US-EXP-33 */
	'saved_search.auto_refresh_disabled': {
		icon: 'NotificationsOff',
		color: 'warning',
		titleKey: 'messages.saved_search.auto_refresh_disabled',
		bodyKey: 'messages.saved_search.auto_refresh_disabled_body',
		buildRoute: ({ data }) =>
			data.searchId ? `/company/searches/${data.searchId}` : '/company/searches',
	},

	// ── Smart Collection auto-refresh (US-SCOLL-06) ───────────────────
	/** @userstory US-SCOLL-06 */
	'smart_collection.new_matches': {
		icon: 'AutoAwesome',
		color: 'success',
		titleKey: 'messages.smart_collection.new_matches',
		bodyKey: 'messages.smart_collection.new_matches_body',
		buildRoute: ({ data }) =>
			data.smartCollectionId
				? `/company/smart-collections/${data.smartCollectionId}`
				: '/company/smart-collections',
	},
	/** @userstory US-SCOLL-06 */
	'smart_collection.auto_refresh_disabled': {
		icon: 'NotificationsOff',
		color: 'warning',
		titleKey: 'messages.smart_collection.auto_refresh_disabled',
		bodyKey: 'messages.smart_collection.auto_refresh_disabled_body',
		buildRoute: ({ data }) =>
			data.smartCollectionId
				? `/company/smart-collections/${data.smartCollectionId}`
				: '/company/smart-collections',
	},

	// ── Opening application status change — anti-ghosting (US-OPEN-16) ─────
	/** @userstory US-OPEN-16 */
	'opening.application_status_changed': {
		icon: 'Assignment',
		color: 'info',
		titleKey: 'messages.opening.application_status_changed',
		bodyKey: 'messages.opening.application_status_changed_body',
		// Talent-facing: deep-link to My Applications, where the candidate-safe status
		// chip is rendered. The payload carries only the mapped status — never the raw stage.
		buildRoute: () => '/talent/applications',
	},

	// ── External Application Link (Plan §6) ───────────────────────────────
	/** @userstory US-LNK-08 */
	'external_link.application_submitted': {
		icon: 'PersonAdd',
		color: 'info',
		titleKey: 'messages.external_link.application_submitted',
		bodyKey: 'messages.external_link.application_submitted_body',
		// Non-navigable: the dedicated /company/links submissions view was removed.
		// Application links now originate from AI Searches / Openings.
	},

	// ── Future producers (Plan §4/§5/§7) — uncomment when the backend emits them.
	// 'feedback.requested': {
	// 	icon: 'RateReview',
	// 	color: 'warning',
	// 	titleKey: 'messages.feedback.requested',
	// 	buildRoute: ({ data, role }) =>
	// 		role === UserType.COMPANY
	// 			? `/company/feedback/${data.feedbackId ?? ''}`
	// 			: `/talent/feedback/${data.feedbackId ?? ''}`,
	// },
	// 'external_link.application_submitted': {
	// 	icon: 'OpenInNew',
	// 	color: 'info',
	// 	titleKey: 'messages.external_link.application_submitted',
	// 	buildRoute: () => '/talent/applications',
	// },
};

/** Looks up the descriptor for a `type`, falling back to {@link GENERIC_DESCRIPTOR}. */
export function resolveNotificationDescriptor(type: string): NotificationTypeDescriptor {
	return NOTIFICATION_TYPES[type] ?? GENERIC_DESCRIPTOR;
}
